import { useAtom, useAtomValue } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import type { TextInput } from 'react-native';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    HelpCircleIcon,
    hitSlop,
    Icon,
    LabelRegular,
    Sheet,
    StyledBodyMediumRegularInputNoOutline,
    tokens,
    View,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import { uuid } from '../../../common/misc';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import {
    incomeSplitAtom,
    incomeSplitRuleSetEditorModeAtom,
    selectedRuleSetAtom,
} from '../../state/PaycheckRoutingAtoms';

const INPUT_STYLE_PROPS = {
    backgroundColor: tokens.color.light.gray.gray200,
    padding: 16,
    height: 48,
    borderWidth: 1,
    borderColor: tokens.color.light.gray.gray600,
    borderRadius: 16,
};

interface IncomeSplitEditRuleSetSheetProps {
    isOpen: boolean;
    onClose: () => void;
}

const IncomeSplitEditRuleSetSheet = ({
    isOpen,
    onClose,
}: IncomeSplitEditRuleSetSheetProps) => {
    const { trackViewOnce } = useViewEvents();
    const [incomeSplit, setIncomeSplit] = useAtom(incomeSplitAtom);
    const [selectedRuleSet, setSelectedRuleSet] = useAtom(selectedRuleSetAtom);
    const [ruleSetName, setRuleSetName] = useState(selectedRuleSet?.name ?? '');
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const editorMode = useAtomValue(incomeSplitRuleSetEditorModeAtom);
    const inputRef = useRef<TextInput | null>(null);

    const [showRuleSetTip, setShowRuleSetTip] = useState(true);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 300);
        }
    }, [isOpen]);

    useEffect(() => {
        setRuleSetName(selectedRuleSet?.name ?? '');
    }, [selectedRuleSet]);

    useEffect(() => {
        if (isOpen)
            trackViewOnce('Banking/IncomeSplit/IncomeSplitEditRuleSetSheet');
    }, [isOpen]);

    const handleOnClose = () => {
        setRuleSetName('');
        setSelectedRuleSet(null);
        onClose();
    };

    const handleDeleteRuleSet = () => {
        if (!incomeSplit || !selectedRuleSet) return;
        setIncomeSplit({
            ...incomeSplit,
            ruleSets: incomeSplit.ruleSets.filter(
                (group) => group.id !== selectedRuleSet.id,
            ),
        });
        setIsConfirmingDelete(false);
        handleOnClose();
    };

    const handleSave = () => {
        if (!incomeSplit) return;
        const isEditing = editorMode === 'Edit';
        setIncomeSplit({
            ...incomeSplit,
            ruleSets: isEditing
                ? incomeSplit.ruleSets.map((group) =>
                      group.id === selectedRuleSet?.id
                          ? { ...group, name: ruleSetName }
                          : group,
                  )
                : [
                      ...incomeSplit.ruleSets,
                      {
                          id: uuid(),
                          incomeSplitConfigId: incomeSplit.id ?? uuid(),
                          name: ruleSetName,
                          priority: incomeSplit.ruleSets.length + 1,
                          rules: [],
                      },
                  ],
        });
        handleOnClose();
    };

    const renderDeleteConfirmation = () => (
        <YStack gap={32} alignItems="center" marginTop={8}>
            <YStack gap={4}>
                <BodyLargeRegular>
                    Are you sure you want to delete this rule set?
                </BodyLargeRegular>
                <BodyMediumRegular color="$gray1000">
                    You will also delete any rules associated with this rule
                    set.
                </BodyMediumRegular>
            </YStack>
            <YStack gap={12} width="100%">
                <Button
                    variant={ButtonVariant.PRIMARY}
                    tone="danger"
                    size={ButtonSize.LARGE}
                    text="Yes, delete rule set and associated rules"
                    onPress={handleDeleteRuleSet}
                />
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.LARGE}
                    text="Cancel"
                    onPress={() => {
                        setIsConfirmingDelete(false);
                    }}
                />
            </YStack>
        </YStack>
    );

    const renderEditor = () => (
        <>
            {editorMode === 'Add' && showRuleSetTip && (
                <YStack
                    backgroundColor="$green300"
                    gap={8}
                    padding={12}
                    borderRadius={8}
                    maxWidth={500}
                >
                    <XStack alignItems="center" justifyContent="space-between">
                        <XStack gap={8} alignItems="center">
                            <Icon
                                icon={HelpCircleIcon}
                                size={16}
                                color="$green1100"
                            />
                            <BodySmallEmphasized color="$green1100">
                                New to rule sets?
                            </BodySmallEmphasized>
                        </XStack>
                        <View
                            cursor="pointer"
                            {...hitSlop(8)}
                            onPress={() => {
                                setShowRuleSetTip(false);
                            }}
                        >
                            <Icon icon={XIcon} color="$green1100" />
                        </View>
                    </XStack>
                    <BodySmallRegular color="$green1100">
                        Think of a rule set as a container for rules that all
                        run at the same time. You’ll assign a percentage to each
                        rule, and those percentages must add up to 100%.
                    </BodySmallRegular>
                </YStack>
            )}
            <YStack gap={8}>
                <LabelRegular color="$gray1100">Rule set name</LabelRegular>
                <StyledBodyMediumRegularInputNoOutline
                    ref={inputRef}
                    value={ruleSetName}
                    onChangeText={setRuleSetName}
                    {...INPUT_STYLE_PROPS}
                />
            </YStack>
            <XStack gap={12}>
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.LARGE}
                    text="Cancel"
                    onPress={handleOnClose}
                    buttonProps={{ flexShrink: 1 }}
                />
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text={editorMode === 'Add' ? 'Add' : 'Save'}
                    onPress={handleSave}
                    buttonProps={{ flexShrink: 1 }}
                />
            </XStack>
        </>
    );

    return (
        <Sheet isOpen={isOpen} onClose={handleOnClose} showCloseButton={false}>
            <YStack $sm={{ minWidth: 400 }} gap={24}>
                {isConfirmingDelete ? (
                    renderDeleteConfirmation()
                ) : (
                    <>
                        <XStack
                            width="100%"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <View width={48} />
                            <BodyLargeRegular textAlign="center">
                                {editorMode} rule set
                            </BodyLargeRegular>
                            {editorMode === 'Edit' ? (
                                <XStack
                                    width="auto"
                                    height="100%"
                                    alignItems="center"
                                    justifyContent="flex-end"
                                >
                                    <Button
                                        variant={ButtonVariant.TERTIARY}
                                        size={ButtonSize.SMALL}
                                        width="auto"
                                        tone="danger"
                                        text="Delete"
                                        onPress={() => {
                                            setIsConfirmingDelete(true);
                                        }}
                                    />
                                </XStack>
                            ) : (
                                <View width={48} />
                            )}
                        </XStack>

                        {renderEditor()}
                    </>
                )}
            </YStack>
        </Sheet>
    );
};

export default IncomeSplitEditRuleSetSheet;
